import axios from "axios";

// Create axios instance
const apiBaseURL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api/v1";
console.log("🌐 API Base URL:", apiBaseURL);
console.log("🔧 Environment:", process.env.NODE_ENV);

const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Check both localStorage and sessionStorage for token
    const token =
      sessionStorage.getItem("token") || localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear all auth data
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      localStorage.removeItem("user");

      // Only redirect if not already on login page
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials, rememberMe = false) =>
    api.post("/auth/login", { ...credentials, rememberMe }),
  register: (userData, rememberMe = false) =>
    api.post("/auth/register", { ...userData, rememberMe }),
  getCurrentUser: () => api.get("/auth/me"),
  changePassword: (passwordData) =>
    api.post("/auth/change-password", passwordData),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) =>
    api.post("/auth/reset-password", { token, password }),
  verifyToken: (token) =>
    api.get("/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// Users API
export const usersAPI = {
  // Regular user functions
  getProfile: () => api.get("/users/profile"),
  updateProfile: (userData) => api.put("/users/profile", userData),
  getAddresses: () => api.get("/users/addresses"),
  addAddress: (addressData) => api.post("/users/addresses", addressData),
  updateAddress: (id, addressData) =>
    api.put(`/users/addresses/${id}`, addressData),
  deleteAddress: (id) => api.delete(`/users/addresses/${id}`),
  getDietaryPreferences: () => api.get("/users/dietary-preferences"),
  addDietaryPreference: (preferenceData) =>
    api.post("/users/dietary-preferences", preferenceData),
  deleteDietaryPreference: (id) =>
    api.delete(`/users/dietary-preferences/${id}`),
  // Employee management functions (Admin only)
  getEmployees: (params) => api.get("/users/employees", { params }),
  createEmployee: (employeeData) => api.post("/users/employees", employeeData),
  updateEmployee: (id, employeeData) =>
    api.put(`/users/employees/${id}`, employeeData),
  deleteEmployee: (id) => api.delete(`/users/employees/${id}`),
  sendWelcomeEmail: (id) =>
    api.post(`/users/employees/${id}/send-welcome-email`),
  // Mass notification (Admin only)
  sendMassNotification: (notificationData) =>
    api.post("/users/admin/send-mass-notification", notificationData),
};

// Vendors API
export const vendorsAPI = {
  getVendors: (params) => api.get("/vendors", { params }),
  getVendor: (id) => api.get(`/vendors/${id}`),
  applyToVendor: (vendorData) => api.post("/vendors/apply", vendorData),
  getMyVendor: () => api.get("/vendors/my-vendor"),
  updateMyVendor: (vendorData) => api.put("/vendors/my-vendor", vendorData),
  getSubscribers: () => api.get("/vendors/subscribers"),
  addVendorReview: (vendorId, reviewData) =>
    api.post(`/vendors/${vendorId}/reviews`, reviewData),
  // Admin functions
  createVendor: (vendorData) => api.post("/vendors", vendorData),
  updateVendor: (id, vendorData) => api.put(`/vendors/${id}`, vendorData),
  deleteVendor: (id) => api.delete(`/vendors/${id}`),
  approveVendor: (id) => api.put(`/vendors/${id}/approve`),
  rejectVendor: (id) => api.put(`/vendors/${id}/reject`),
  suspendVendor: (id) => api.put(`/vendors/${id}/suspend`),
  sendWelcomeEmail: (id) => api.post(`/vendors/${id}/send-welcome-email`),
  getAdminStats: () => api.get("/vendors/admin/stats"),
};

// Meal Plans API
export const mealPlansAPI = {
  getMealPlans: (params) => api.get("/meal-plans", { params }),
  getMealPlan: (id) => api.get(`/meal-plans/${id}`),
  getVendorMealPlan: (id) => api.get(`/meal-plans/vendor/${id}`),
  createMealPlan: (mealPlanData) => api.post("/meal-plans", mealPlanData),
  updateMealPlan: (id, mealPlanData) =>
    api.put(`/meal-plans/${id}`, mealPlanData),
  deleteMealPlan: (id) => api.delete(`/meal-plans/${id}`),
  addMealPlanItem: (id, itemData) =>
    api.post(`/meal-plans/${id}/items`, itemData),
  // Vendor-specific functions
  getVendorMealPlans: (params) => api.get("/meal-plans/vendor", { params }),
  getVendorMealPlan: (id) => api.get(`/meal-plans/vendor/${id}`),
  createVendorMealPlan: (mealPlanData) =>
    api.post("/meal-plans/vendor", mealPlanData),
  updateVendorMealPlan: (id, mealPlanData) =>
    api.put(`/meal-plans/vendor/${id}`, mealPlanData),
  deleteVendorMealPlan: (id) => api.delete(`/meal-plans/vendor/${id}`),
  toggleMealPlanStatus: (id) =>
    api.put(`/meal-plans/vendor/${id}/toggle-status`),
  getMealPlanSubscribers: (id) =>
    api.get(`/meal-plans/vendor/${id}/subscribers`),
  getMealPlanAnalytics: (id) => api.get(`/meal-plans/vendor/${id}/analytics`),
  // Admin-specific functions
  getAllMealPlans: (params) => api.get("/meal-plans/admin/all", { params }),
};

// Meals API (Standalone meals)
export const mealsAPI = {
  getMeals: (params) => api.get("/meals", { params }),
  getMeal: (id) => api.get(`/meals/${id}`),
  // Vendor-specific functions
  getVendorMeals: () => api.get("/meals/vendor"),
  createMeal: (mealData) => api.post("/meals", mealData),
  updateMeal: (id, mealData) => api.put(`/meals/${id}`, mealData),
  deleteMeal: (id) => api.delete(`/meals/${id}`),
};

// Orders API
export const ordersAPI = {
  getOrders: (params) => api.get("/orders", { params }),
  getOrder: (id) => api.get(`/orders/${id}`),
  getVendorOrder: (id) => api.get(`/orders/vendor/${id}`),
  getAdminOrder: (id) => api.get(`/orders/admin/${id}`),
  updateOrderStatus: (id, statusData) =>
    api.put(`/orders/${id}/status`, statusData),
  addOrderReview: (id, reviewData) =>
    api.post(`/orders/${id}/review`, reviewData),
  getVendorOrders: (params) => api.get("/orders/vendor/orders", { params }),
  getAllOrders: (params) => api.get("/orders/admin/all", { params }),
  createMealOrder: (orderData) => api.post("/orders/meal", orderData),
  trackOrder: (trackingNumber) => api.get(`/orders/track/${trackingNumber}`),
  assignDriver: (id, driverData) =>
    api.put(`/orders/${id}/assign-driver`, driverData),
  getOrderStatusHistory: (id) => api.get(`/orders/${id}/status-history`),
};

// Subscriptions API
export const subscriptionsAPI = {
  getSubscriptions: () => api.get("/subscriptions"),
  getSubscription: (id) => api.get(`/subscriptions/${id}`),
  createSubscription: (subscriptionData) =>
    api.post("/subscriptions", subscriptionData),
  updateSubscriptionStatus: (id, status) =>
    api.put(`/subscriptions/${id}/status`, { status }),
  getSubscriptionOrders: (id) => api.get(`/subscriptions/${id}/orders`),
  customizeSubscription: (id, customizations) =>
    api.put(`/subscriptions/${id}/customize`, customizations),
  cancelSubscription: (id) => api.put(`/subscriptions/${id}/cancel`),
  deleteSubscription: (id) => api.delete(`/subscriptions/${id}`),
};

// Payments API
export const paymentsAPI = {
  getPayments: (params) => api.get("/payments", { params }),
  createPayment: (paymentData) => api.post("/payments/create", paymentData),
  confirmPayment: (id, paymentData) =>
    api.post(`/payments/${id}/confirm`, paymentData),
  processRefund: (id, refundData) =>
    api.post(`/payments/${id}/refund`, refundData),
};

// Transactions API (used for payment history/receipts)
export const transactionsAPI = {
  getTransactions: (params) => api.get("/transactions", { params }),
  getTransaction: (id) => api.get(`/transactions/${id}`),
  getTransactionSummary: () => api.get("/transactions/summary"),
};

// Delivery API
export const deliveryAPI = {
  getDrivers: () => api.get("/delivery/drivers"),
  registerDriver: (driverData) => api.post("/delivery/drivers", driverData),
  updateDriverStatus: (id, statusData) =>
    api.put(`/delivery/drivers/${id}/status`, statusData),
  updateDriverLocation: (id, locationData) =>
    api.put(`/delivery/drivers/${id}/location`, locationData),
  getDriverOrders: (id, params) =>
    api.get(`/delivery/drivers/${id}/orders`, { params }),
};

// Upload API
export const uploadAPI = {
  uploadImage: (formData) =>
    api.post("/upload/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  uploadMultipleImages: (formData) =>
    api.post("/upload/multiple", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
};

// Ingredients API
export const ingredientsAPI = {
  searchIngredients: (params) => api.get("/ingredients", { params }),
  createIngredient: (ingredientData) =>
    api.post("/ingredients", ingredientData),
  getCategories: () => api.get("/ingredients/categories"),
};

export default api;
